<script>
  import { questionsetStore, isSelfNodeQuestion, getRelationTypeFromDataset } from '../../../stores/questionsetStore';
  
  export let question;
  export let updateWorkingQuestion;
  export let hasUnsavedChanges;
  
  function isPropsQuestion() {
    if (!question.dataset || !Array.isArray(question.dataset)) return false;
    return question.dataset.some((item) => 
      item[0] === "selfProps" || item[0] === "nodeProps"
    ) && !question.dataset.some((item) => item[0] === "setSelf");
  }
  
  function canQuestionTypeBeSelfNode(questionType) {
    const allowedTypes = ['Text', 'CheckBox', 'Select'];
    return allowedTypes.includes(questionType);
  }
  
  $: selfNodeInfo = $questionsetStore ? questionsetStore.getSelfNodeInfo() : { exists: false, index: -1 };
  $: isCurrentSelfNode = isSelfNodeQuestion(question);
  $: isProps = isPropsQuestion();
  $: relationshipType = getRelationTypeFromDataset(question.dataset, question);
  $: canTypeBecomeSelfNode = canQuestionTypeBeSelfNode(question.kind);
  
  // Props questions cannot become self nodes
  $: canBecomeSelfNode = !isCurrentSelfNode && 
                         !isProps && 
                         canTypeBecomeSelfNode && 
                         (!selfNodeInfo.exists || selfNodeInfo.index !== question.id);
  
  // This handles the case where we just converted FROM self node but haven't saved yet
  $: canConvertBackToSelfNode = !isCurrentSelfNode && 
                                selfNodeInfo.exists && 
                                selfNodeInfo.index === question.id;
  
  function convertToSelfNode() {
    const confirmCallback = (existingIndex) => {
      return confirm(`Question ${existingIndex} is currently the self node. Replace it with this question?`);
    };
    
    // Extract the question index from the ID (format: "question--X")
    const questionIndex = parseInt(question.id.split('--')[1]);
    
    // Use the store's convertToSelfNode method which handles everything
    questionsetStore.convertToSelfNode(questionIndex, confirmCallback);
    
    hasUnsavedChanges = true;
  }
  
  function convertFromSelfNode() {
    const confirmCallback = () => {
      return confirm('Remove self node status? This will convert it to a regular node creation question.');
    };
    
    // Extract the question index from the ID (format: "question--X")
    const questionIndex = parseInt(question.id.split('--')[1]);
    
    // Use the store's removeSelfNodeStatus method
    questionsetStore.removeSelfNodeStatus(questionIndex, confirmCallback);
    
    hasUnsavedChanges = true;
  }
</script>

<div class="edit-section" class:selfnode-section={isCurrentSelfNode}>
  <h4>
    Relationship Type: <strong>{relationshipType}</strong>
    {#if isCurrentSelfNode}
      <span class="selfnode-badge">SELF NODE</span>
    {/if}
  </h4>
  
  <div class="form-styled">
    {#if isCurrentSelfNode}
      <div class="selfnode-info">
        <p>✓ This question creates and configures the self node</p>
        <p class="selfnode-description">
          The self node represents the main entity in your network (usually an organization). 
          Only one question can be the self node.
        </p>
        <button class="btn btn-warning" on:click={convertFromSelfNode}>
          Convert to Regular Node Question
        </button>
      </div>
    {:else}
      <div class="relationship-controls">
        <p class="relationship-info">
          Current: <strong>{relationshipType}</strong>
        </p>
        <p class="relationship-description">
          {#if relationshipType === 'createNodes'}
            This question creates new nodes in the network
          {:else if relationshipType === 'addProps'}
            This question adds properties to existing nodes
          {:else if relationshipType === 'selfProps'}
            This question adds properties to the self node
          {:else}
            Unknown relationship type
          {/if}
        </p>
        
        {#if canConvertBackToSelfNode}
          <button class="btn btn-secondary" on:click={convertToSelfNode}>
            Convert Back to Self Node
          </button>
          <p class="convert-help">
            Convert this question back to being the self node (unsaved changes).
          </p>
        {:else if canBecomeSelfNode}
          <button class="btn btn-secondary" on:click={convertToSelfNode}>
            Convert to Self Node
          </button>
          <p class="convert-help">
            Converting to self node will change this question to create and configure 
            the main entity in your network.
            {#if selfNodeInfo.exists}
              This will replace question {selfNodeInfo.index} as the self node.
            {/if}
          </p>
        {:else}
          <!-- Show why conversion is not possible -->
          {#if isProps}
            <p class="warning-text">
              ❌ Props questions cannot become self nodes
            </p>
            <p class="restriction-help">
              Props questions add properties to existing nodes. To create a self node, 
              you need a node creation question (Text, CheckBox, or Select with nodes dataset).
            </p>
          {:else if !canTypeBecomeSelfNode}
            <p class="warning-text">
              ❌ {question.kind} questions cannot become self nodes
            </p>
            <p class="restriction-help">
              Only Text, CheckBox, and Select questions can be self nodes.
            </p>
          {:else if selfNodeInfo.exists && selfNodeInfo.index !== question.id}
            <p class="warning-text">
              Cannot convert: Question {selfNodeInfo.index} is already the self node
            </p>
          {/if}
        {/if}
      </div>
    {/if}
  </div>
</div>

<style lang="scss">
  .edit-section {
    border: 4px solid #d4d3bc;
    border-radius: 0;
    padding: 15px;
    margin-bottom: 20px;
    background: #f3f2e3;
    
    h4 {
      margin-bottom: 15px;
      font-weight: bold;
      font-size: 0.9rem;
      display: flex;
      align-items: center;
    }
    
    &.selfnode-section {
      background: #ecfdf5;
      border-color: #10b981;
    }
  }
  
  .selfnode-badge {
    background: #059669;
    color: white;
    padding: 2px 8px;
    border-radius: 12px;
    font-size: 10px;
    font-weight: bold;
    letter-spacing: 0.5px;
    margin-left: 8px;
  }
  
  .selfnode-info {
    p {
      color: #059669;
      font-weight: bold;
      margin-bottom: 8px;
      font-size: 0.8rem;
      
      &.selfnode-description {
        font-weight: normal;
        color: #065f46;
        font-style: italic;
        margin-bottom: 15px;
      }
    }
  }
  
  .relationship-controls {
    .relationship-info {
      font-size: 0.8rem;
      margin-bottom: 8px;
      color: #374151;
    }
    
    .relationship-description {
      font-size: 0.75rem;
      color: #6b7280;
      font-style: italic;
      margin-bottom: 15px;
    }
    
    .convert-help {
      font-size: 0.7rem;
      color: #6b7280;
      margin-top: 8px;
      font-style: italic;
    }
    
    .restriction-help {
      font-size: 0.7rem;
      color: #6b7280;
      margin-top: 4px;
      font-style: italic;
      line-height: 1.3;
    }
  }
  
  .warning-text {
    color: #dc2626;
    font-size: 0.8rem;
    font-style: italic;
    font-weight: 500;
    margin-bottom: 4px;
  }
  
  .btn {
    padding: 8px 16px;
    border: none;
    border-radius: 0;
    cursor: pointer;
    font-size: 0.75rem;
    font-weight: bold;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    transition: all 0.2s ease;
    
    &.btn-secondary {
      background: #10b981;
      color: white;
      
      &:hover {
        background: #059669;
      }
    }
    
    &.btn-warning {
      background: #f59e0b;
      color: white;
      
      &:hover {
        background: #d97706;
      }
    }
  }
</style>